/**
 * Growth-anchored scheduling for indoor-sown plants.
 *
 * The seasonal calendar (week_offset from season start) is great for *when to sow*,
 * but once a seed has germinated indoors, what happens next is driven by the plant's
 * actual growth — not by the calendar. A vigorous tomato or fennel needs to go out
 * when it's mature, not in 4 weeks because that's what the spreadsheet says.
 *
 * After germination is confirmed, this module reschedules:
 *   - first_transplant  (~2 weeks after germination)
 *   - second_transplant (~4 weeks after germination)
 *   - harden_off        (~5-6 weeks after germination)
 *   - plant_out         (~6-7 weeks after germination, OR after last frost — whichever later)
 *
 * For tender crops, plant_out is floored to the zone's last-frost date so we never
 * push tomatoes out into a frost.
 */

import { addDays, addWeeks, format, parseISO, max as dateMax } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

/** Tasks downstream of germination that should reschedule relative to germination date */
export const GROWTH_ANCHORED_STAGES = [
  'first_transplant',
  'second_transplant',
  'harden_off',
  'plant_out',
] as const;

/** Default day-offset from germination for each downstream stage */
const STAGE_DAYS_FROM_GERMINATION: Record<string, number> = {
  first_transplant: 14,   // pot on after ~2 weeks (true leaves)
  second_transplant: 28,  // larger pot ~4 weeks
  harden_off: 35,         // start hardening ~5 weeks
  plant_out: 42,          // plant out ~6 weeks (subject to frost floor)
};

/** Tender crops that must NOT be planted out before last frost */
const TENDER_PLANTS = new Set([
  'tomatoes', 'tomato', 'cherry tomatoes',
  'courgettes', 'courgette', 'zucchini',
  'peppers', 'pepper', 'chilli', 'chillies', 'chili',
  'aubergine', 'aubergines', 'eggplant',
  'cucumber', 'cucumbers',
  'french beans', 'runner beans', 'climbing beans', 'dwarf beans',
  'sweetcorn', 'corn',
  'basil',
  'florence fennel', 'fennel',
  'pumpkin', 'pumpkins', 'squash', 'butternut squash',
  'melon', 'melons',
]);

/** Approx last frost date per UK zone (month is 0-indexed) */
const LAST_FROST_BY_ZONE: Record<string, { month: number; day: number }> = {
  'UK-South':    { month: 4, day: 7 },   // ~ 7 May
  'UK-Wales':    { month: 4, day: 14 },  // ~14 May
  'UK-Midlands': { month: 4, day: 14 },  // ~14 May
  'UK-NI':       { month: 4, day: 21 },  // ~21 May
  'UK-North':    { month: 4, day: 28 },  // ~28 May
  'UK-Scotland': { month: 5, day: 4 },   // ~ 4 Jun
};

export function isTenderPlant(plantName: string): boolean {
  return TENDER_PLANTS.has(plantName.trim().toLowerCase());
}

export function getLastFrostDate(zone: string, year: number = new Date().getFullYear()): Date {
  const cfg = LAST_FROST_BY_ZONE[zone] || LAST_FROST_BY_ZONE['UK-Midlands'];
  return new Date(year, cfg.month, cfg.day);
}

/**
 * Reschedule downstream growth-anchored tasks for a given user_plant after germination.
 * Returns the number of tasks updated.
 */
export async function rescheduleAfterGermination(params: {
  userPlantId: string;
  germinationDate: Date;
  plantName: string;
  zone: string;
}): Promise<number> {
  const { userPlantId, germinationDate, plantName, zone } = params;

  // Fetch all pending downstream tasks for this plant
  const { data: tasks } = await supabase.from('garden_tasks')
    .select('id, task_type, scheduled_date')
    .eq('user_plant_id', userPlantId)
    .eq('completed', false)
    .in('task_type', GROWTH_ANCHORED_STAGES as unknown as string[]);

  if (!tasks || tasks.length === 0) return 0;

  const tender = isTenderPlant(plantName);
  const lastFrost = getLastFrostDate(zone);

  let updated = 0;
  for (const task of tasks) {
    const daysOffset = STAGE_DAYS_FROM_GERMINATION[task.task_type] ?? 35;
    let newDate = addDays(germinationDate, daysOffset);

    // Frost floor: tender crops can't go out before last frost
    if (task.task_type === 'plant_out' && tender) {
      newDate = dateMax([newDate, lastFrost]);
    }

    const newDateStr = format(newDate, 'yyyy-MM-dd');
    if (newDateStr !== task.scheduled_date) {
      await supabase.from('garden_tasks')
        .update({ scheduled_date: newDateStr } as any)
        .eq('id', task.id);
      updated++;
    }
  }

  return updated;
}

/**
 * Pull a "ready now" trigger forward — used when the gardener manually says
 * a plant is ready to harden off / plant out NOW.
 *
 * Moves the named task to today, and shifts following stages proportionally.
 */
export async function markPlantReadyNow(params: {
  userPlantId: string;
  taskType: 'harden_off' | 'plant_out';
  plantName: string;
  zone: string;
}): Promise<void> {
  const { userPlantId, taskType, plantName, zone } = params;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Frost guard
  if (taskType === 'plant_out' && isTenderPlant(plantName)) {
    const lastFrost = getLastFrostDate(zone);
    if (today < lastFrost) {
      throw new Error(
        `It's still too early to plant ${plantName} outside in ${zone} — risk of frost until ${format(lastFrost, 'd MMM')}.`
      );
    }
  }

  // Move the trigger task to today
  const todayStr = format(today, 'yyyy-MM-dd');
  await supabase.from('garden_tasks')
    .update({ scheduled_date: todayStr } as any)
    .eq('user_plant_id', userPlantId)
    .eq('task_type', taskType)
    .eq('completed', false);

  // For harden_off → also pull plant_out forward by ~7 days from today
  if (taskType === 'harden_off') {
    const plantOutDate = addDays(today, 7);
    let finalDate = plantOutDate;
    if (isTenderPlant(plantName)) {
      finalDate = dateMax([plantOutDate, getLastFrostDate(zone)]);
    }
    await supabase.from('garden_tasks')
      .update({ scheduled_date: format(finalDate, 'yyyy-MM-dd') } as any)
      .eq('user_plant_id', userPlantId)
      .eq('task_type', 'plant_out')
      .eq('completed', false);
  }
}
